import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./NewShortsPlayer.module.css";
import useAuthStore from "../../store/authStore";
import { useToggleLike } from "../../hooks/useGetShorts";
import { Heart } from "lucide-react";

const NewShortsPlayer = ({ shorts = [], initialShortSlug }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false); // Start as false for iOS
  const [isMuted, setIsMuted] = useState(true); // Start muted for iOS autoplay
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [autoplayFailed, setAutoplayFailed] = useState(true); // Start as true
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [localLikesCount, setLocalLikesCount] = useState(0);

  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const startTouchY = useRef(0);
  const currentTouchY = useRef(0);
  const isDragging = useRef(false);
  const wheelTimeoutRef = useRef(null);
  const isScrolling = useRef(false);
  const touchStartTime = useRef(0);
  const retryCount = useRef(0);

  // Detect iOS
  useEffect(() => {
    const detectIOS = () => {
      return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      );
    };
    setIsIOS(detectIOS());
  }, []);

  // Find initial short index
  useEffect(() => {
    const lookupSlug = initialShortSlug || slug;
    if (lookupSlug && shorts.length > 0) {
      const index = shorts.findIndex((short) => short.slug === lookupSlug);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [initialShortSlug, slug, shorts]);

  const currentShort = shorts[currentIndex];
  const { isAuthenticated } = useAuthStore();

  // Like functionality hook
  const {
    isLiked,
    toggleLike,
    isLoading: isLikeLoading,
  } = useToggleLike(currentShort?.id);

  useEffect(() => {
    if (currentShort?.likes_count !== undefined) {
      setLocalLikesCount(currentShort.likes_count);
    }
  }, [currentShort?.id, currentShort?.likes_count]);

  // Add this handler function
  const handleLike = () => {
    if (!isAuthenticated || !currentShort?.id || isLikeLoading) return;

    // Optimistically update the UI immediately
    if (isLiked) {
      // If currently liked, decrease count
      setLocalLikesCount((prev) => Math.max(0, prev - 1));
    } else {
      // If not liked, increase count
      setLocalLikesCount((prev) => prev + 1);
    }

    toggleLike();
  };

  // Enhanced iOS video setup and event prevention
  useEffect(() => {
    const preventBehavior = (e) => {
      // Allow video element interactions but prevent body scrolling
      if (e.target !== videoRef.current) {
        e.preventDefault();
      }
    };

    const preventOverscroll = (e) => {
      e.preventDefault();
    };

    const preventZoom = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    // Prevent various iOS behaviors
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";
    document.body.style.touchAction = "none";
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";

    // Prevent zoom and scroll
    const viewport = document.querySelector("meta[name=viewport]");
    const originalViewport = viewport?.getAttribute("content");
    if (viewport) {
      viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
      );
    }

    // Add event listeners with more specific targeting
    document.addEventListener("touchmove", preventBehavior, { passive: false });
    document.addEventListener("wheel", preventOverscroll, { passive: false });
    document.addEventListener("gesturestart", preventZoom, { passive: false });
    document.addEventListener("gesturechange", preventZoom, { passive: false });
    document.addEventListener("gestureend", preventZoom, { passive: false });

    return () => {
      document.body.style.overscrollBehavior = "auto";
      document.documentElement.style.overscrollBehavior = "auto";
      document.body.style.touchAction = "auto";
      document.body.style.userSelect = "auto";
      document.body.style.webkitUserSelect = "auto";

      if (viewport && originalViewport) {
        viewport.setAttribute("content", originalViewport);
      }

      document.removeEventListener("touchmove", preventBehavior);
      document.removeEventListener("wheel", preventOverscroll);
      document.removeEventListener("gesturestart", preventZoom);
      document.removeEventListener("gesturechange", preventZoom);
      document.removeEventListener("gestureend", preventZoom);
    };
  }, []);

  // Auto-hide controls after 3 seconds
  useEffect(() => {
    if (showControls && hasUserInteracted) {
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
    return () => clearTimeout(controlsTimeoutRef.current);
  }, [showControls, hasUserInteracted]);

  // Enhanced video play attempt with iOS-specific handling
  const attemptPlay = async () => {
    if (!videoRef.current) return;

    try {
      setVideoError(false);

      const video = videoRef.current;

      // Essential iOS configuration - must be set before any play attempt
      video.muted = isMuted;
      video.playsInline = true;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");

      // For iOS, ensure we have user interaction before attempting play
      if (isIOS && !hasUserInteracted) {
        setAutoplayFailed(true);
        setShowControls(true);
        return;
      }

      // Wait for video to be sufficiently loaded for iOS
      if (video.readyState < 2) {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(
            () => reject(new Error("Video load timeout")),
            10000
          );

          const handleCanPlay = () => {
            clearTimeout(timeout);
            video.removeEventListener("canplaythrough", handleCanPlay);
            video.removeEventListener("loadeddata", handleCanPlay);
            video.removeEventListener("error", handleError);
            resolve();
          };

          const handleError = (e) => {
            clearTimeout(timeout);
            video.removeEventListener("canplaythrough", handleCanPlay);
            video.removeEventListener("loadeddata", handleCanPlay);
            video.removeEventListener("error", handleError);
            reject(new Error("Video load error: " + e.message));
          };

          // Listen for multiple ready states for better iOS compatibility
          video.addEventListener("canplaythrough", handleCanPlay);
          video.addEventListener("loadeddata", handleCanPlay);
          video.addEventListener("error", handleError);

          // Force load if not already loading
          if (video.networkState === video.NETWORK_IDLE) {
            video.load();
          }
        });
      }

      // iOS-specific: Small delay before play attempt
      if (isIOS) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const playPromise = video.play();
      await playPromise;

      setIsPlaying(true);
      setAutoplayFailed(false);
      retryCount.current = 0;
    } catch (error) {
      console.log("Play attempt failed:", error.name, error.message);
      setIsPlaying(false);
      setAutoplayFailed(true);
      setShowControls(true);

      // iOS fallback: try with muted if not already muted
      if (isIOS && !isMuted && retryCount.current < 2) {
        retryCount.current++;
        console.log("Retrying with muted audio for iOS");
        setIsMuted(true);
        setTimeout(() => attemptPlay(), 500);
      }
    }
  };

  // Enhanced video event handlers
  const handleVideoLoad = () => {
    console.log("Video loaded, readyState:", videoRef.current?.readyState);
    setIsLoading(false);
    setVideoError(false);

    if (videoRef.current) {
      const video = videoRef.current;
      setDuration(video.duration || 0);

      // Critical iOS setup - must be done on every load
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.playsInline = true;
      video.muted = isMuted;

      // iOS-specific: Only attempt autoplay after user interaction
      if (hasUserInteracted && !isPlaying) {
        setTimeout(
          () => {
            if (!isPlaying && video.paused) {
              attemptPlay();
            }
          },
          isIOS ? 300 : 150
        );
      }
    }
  };

  const handleVideoError = (e) => {
    console.error("Video error:", e);
    setVideoError(true);
    setIsLoading(false);
    setAutoplayFailed(true);
    setShowControls(true);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && videoRef.current.duration) {
      const progress =
        (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleVideoEnd = () => {
    goToNext();
  };

  // Navigation functions with better error handling
  const goToPrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      resetVideoState();
      setTimeout(() => {
        if (hasUserInteracted) {
          setIsPlaying(false);
        }
      }, 100);
      navigate(`/shorts/${shorts[newIndex].slug}`, { replace: true });
    }
  };
  const goToNext = () => {
    if (currentIndex < shorts.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      resetVideoState();
      setTimeout(() => {
        if (hasUserInteracted) {
          setIsPlaying(false);
        }
      }, 100);
      navigate(`/shorts/${shorts[newIndex].slug}`, { replace: true });
    }
  };

  const resetVideoState = () => {
    setProgress(0);
    setIsLoading(true);
    setVideoError(false);
    setAutoplayFailed(!hasUserInteracted); // Keep user interaction state
    retryCount.current = 0;

    if (videoRef.current) {
      const video = videoRef.current;
      video.pause();
      video.currentTime = 0;

      // iOS: Force reload to ensure clean state
      if (isIOS) {
        video.load();
      }
    }
  };

  // Enhanced play/pause toggle
  const togglePlayPause = async () => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    if (!videoRef.current) return;

    const video = videoRef.current;

    try {
      if (isPlaying) {
        video.pause();
        setIsPlaying(false);
      } else {
        // Essential iOS preparation
        video.muted = isMuted;
        video.playsInline = true;
        video.setAttribute("playsinline", "true");
        video.setAttribute("webkit-playsinline", "true");

        // iOS-specific: Ensure video is ready
        if (isIOS && video.readyState < 2) {
          console.log("iOS: Video not ready, loading...");
          video.load();
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        const playPromise = video.play();
        await playPromise;

        setIsPlaying(true);
        setAutoplayFailed(false);
      }
    } catch (error) {
      console.log("Toggle play failed:", error.name, error.message);
      setAutoplayFailed(true);
      setShowControls(true);

      // iOS fallback strategy
      if (isIOS && !isMuted && retryCount.current < 2) {
        retryCount.current++;
        console.log("iOS: Retrying with muted audio");
        setIsMuted(true);
        setTimeout(() => togglePlayPause(), 300);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);

      // On iOS, if we unmute, we might need to restart playback
      if (isIOS && !newMutedState && isPlaying) {
        attemptPlay();
      }
    }
  };

  const handlePlayerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    setShowControls(true);

    // iOS requires immediate play attempt on user interaction
    if (isIOS) {
      // For iOS, handle play immediately on click
      if (!isPlaying) {
        togglePlayPause();
      }
    } else {
      togglePlayPause();
    }
  };

  const handleShare = () => {
    setShowShareMenu(!showShareMenu);
  };

  const shareOptions = [
    {
      name: "Copy Link",
      action: () => navigator.clipboard?.writeText(window.location.href),
    },
    {
      name: "WhatsApp",
      action: () =>
        window.open(
          `https://wa.me/?text=${encodeURIComponent(window.location.href)}`
        ),
    },
    {
      name: "Twitter/X",
      action: () =>
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            window.location.href
          )}`
        ),
    },
    {
      name: "Facebook",
      action: () =>
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            window.location.href
          )}`
        ),
    },
  ];

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    navigate(-1);
  };

  // Enhanced touch handlers with iOS-specific improvements
  const handleTouchStart = (e) => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    // Only prevent default for container, not video
    if (e.target !== videoRef.current) {
      e.preventDefault();
    }

    const touch = e.touches[0];
    startTouchY.current = touch.clientY;
    currentTouchY.current = touch.clientY;
    isDragging.current = false;
    touchStartTime.current = Date.now();
  };

  const handleTouchMove = (e) => {
    if (!startTouchY.current) return;

    // Prevent default to stop pull-to-refresh and scrolling
    if (e.target !== videoRef.current) {
      e.preventDefault();
    }

    const touch = e.touches[0];
    currentTouchY.current = touch.clientY;
    const diff = startTouchY.current - currentTouchY.current;

    if (Math.abs(diff) > 15) {
      isDragging.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (!startTouchY.current || !isDragging.current) {
      // Reset values
      startTouchY.current = 0;
      currentTouchY.current = 0;
      isDragging.current = false;
      touchStartTime.current = 0;
      return;
    }

    e.preventDefault();

    const diff = startTouchY.current - currentTouchY.current;
    const threshold = 50;
    const timeThreshold = 500;
    const swipeTime = Date.now() - touchStartTime.current;

    if (Math.abs(diff) > threshold && swipeTime < timeThreshold) {
      if (diff > 0 && currentIndex < shorts.length - 1) {
        goToNext();
      } else if (diff < 0 && currentIndex > 0) {
        goToPrevious();
      }
    }

    // Reset touch values
    startTouchY.current = 0;
    currentTouchY.current = 0;
    isDragging.current = false;
    touchStartTime.current = 0;
  };

  // Mouse wheel handler for desktop
  const handleWheel = (e) => {
    e.preventDefault();

    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    if (isScrolling.current) return;

    const delta = e.deltaY;
    const threshold = 50;

    if (Math.abs(delta) > threshold) {
      isScrolling.current = true;

      if (delta > 0 && currentIndex < shorts.length - 1) {
        goToNext();
      } else if (delta < 0 && currentIndex > 0) {
        goToPrevious();
      }

      clearTimeout(wheelTimeoutRef.current);
      wheelTimeoutRef.current = setTimeout(() => {
        isScrolling.current = false;
      }, 800);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!hasUserInteracted) {
        setHasUserInteracted(true);
      }

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          if (currentIndex > 0) goToPrevious();
          break;
        case "ArrowDown":
          e.preventDefault();
          if (currentIndex < shorts.length - 1) goToNext();
          break;
        case " ":
          e.preventDefault();
          togglePlayPause();
          break;
        case "Escape":
          e.preventDefault();
          handleClose();
          break;
        case "m":
        case "M":
          e.preventDefault();
          toggleMute();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, isPlaying, isMuted, hasUserInteracted]);

  // Wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      return () => container.removeEventListener("wheel", handleWheel);
    }
  }, [currentIndex]);

  // First interaction handler for iOS
  useEffect(() => {
    const handleFirstInteraction = (e) => {
      console.log("First user interaction detected");
      setHasUserInteracted(true);

      // iOS: Immediately try to play if video is loaded and paused
      if (isIOS && videoRef.current && !isLoading && !videoError) {
        setTimeout(() => {
          if (videoRef.current && videoRef.current.paused && autoplayFailed) {
            console.log("iOS: Attempting play after first interaction");
            attemptPlay();
          }
        }, 100);
      }
    };

    // Use more specific events for better iOS detection
    const events = ["touchstart", "click", "keydown"];

    events.forEach((event) => {
      document.addEventListener(event, handleFirstInteraction, {
        once: true,
        passive: event === "touchstart",
        capture: true,
      });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleFirstInteraction, true);
      });
    };
  }, [isIOS, autoplayFailed, isLoading, videoError]);

  // Auto-play when video loads and user has interacted
  useEffect(() => {
    if (
      videoRef.current &&
      hasUserInteracted &&
      !isLoading &&
      !videoError &&
      !isPlaying
    ) {
      const timer = setTimeout(
        () => {
          // Only attempt if video is not already playing
          if (!isPlaying && videoRef.current && videoRef.current.paused) {
            attemptPlay();
          }
        },
        isIOS ? 300 : 200
      ); // Longer delay for iOS
      return () => clearTimeout(timer);
    }
  }, [currentIndex, hasUserInteracted, isLoading, videoError, isPlaying]); // Added isPlaying dependency

  // 6. Add specific iOS play handler
  const handleIOSPlay = async () => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    // For iOS, always ensure we can play
    if (videoRef.current && isIOS) {
      try {
        const video = videoRef.current;
        video.muted = isMuted;
        await video.play();
        setIsPlaying(true);
        setAutoplayFailed(false);
      } catch (error) {
        console.log("iOS play failed:", error);
        // Try with muted
        if (!isMuted) {
          setIsMuted(true);
          setTimeout(() => handleIOSPlay(), 200);
        }
      }
    } else {
      togglePlayPause();
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!currentShort) return null;

  return (
    <div
      className={styles.playerContainer}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.videoWrapper}>
        <video
          ref={videoRef}
          src={currentShort.video_url}
          className={styles.video}
          onClick={handlePlayerClick}
          onLoadedData={handleVideoLoad}
          onLoadedMetadata={handleVideoLoad}
          onCanPlay={handleVideoLoad}
          onCanPlayThrough={handleVideoLoad} // Added for iOS
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleVideoEnd}
          onError={handleVideoError}
          muted={isMuted}
          playsInline={true}
          webkit-playsinline="true"
          x-webkit-airplay="deny" // Prevent AirPlay interference
          preload="metadata"
          controls={false}
          disablePictureInPicture={true}
          autoPlay={false} // Explicitly set to false for iOS
          style={{ outline: "none" }}
          // iOS-specific attributes
          poster="" // Empty poster to prevent iOS from showing thumbnail
        />

        {isLoading && !videoError && (
          <div className={styles.loadingOverlay}>
          </div>
        )}

        {videoError && (
          <div className={styles.loadingOverlay}>
            <div style={{ textAlign: "center", color: "white" }}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="white"
                style={{ marginBottom: "10px" }}
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
              <p style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
                Video Error
              </p>
              <button
                onClick={() => {
                  setVideoError(false);
                  setIsLoading(true);
                  if (videoRef.current) {
                    videoRef.current.load();
                  }
                }}
                style={{
                  background: "#ff4444",
                  border: "none",
                  color: "white",
                  padding: "10px 20px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Enhanced Autoplay Message for iOS */}
        {(autoplayFailed || !hasUserInteracted) &&
          !isLoading &&
          !videoError && (
            <div className={styles.autoplayMessage}>
              <div className={styles.autoplayPrompt}>
                <svg width="80" height="80" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z" />
                </svg>
                <p style={{ fontSize: "18px", fontWeight: "600" }}>
                  {isIOS ? "Tap to start" : "Tap to play"}
                </p>
                {isIOS && (
                  <p
                    style={{
                      fontSize: "14px",
                      opacity: "0.8",
                      marginTop: "5px",
                    }}
                  >
                    Videos start muted on iOS
                  </p>
                )}
              </div>
            </div>
          )}

        {/* Progress Bar */}
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Top Controls */}
        <div
          className={`${styles.topControls} ${
            showControls ? styles.visible : ""
          }`}
        >
          <button className={styles.backButton} onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>
          <div className={styles.shortInfo}>
            <h3 className={styles.shortTitle}>{currentShort.title}</h3>
            <span className={styles.shortCreator}>@{currentShort.creator}</span>
          </div>
        </div>

        {/* Center Play Button */}
        {!isPlaying && showControls && hasUserInteracted && (
          <div className={styles.centerPlayButton} onClick={togglePlayPause}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}

        {/* Side Controls */}
        <div className={styles.sideControls}>
          {isAuthenticated && (
            <div className={styles.actionButton}>
              <button
                className={styles.actionBtn}
                onClick={handleLike}
                disabled={isLikeLoading}
                style={{
                  opacity: isLikeLoading ? 0.6 : 1,
                  transform: isLiked ? "scale(1.1)" : "scale(1)",
                  transition: "all 0.2s ease",
                }}
              >
                <Heart
                  size={28}
                  fill={isLiked ? "#ff3040" : "none"}
                  color={isLiked ? "#ff3040" : "white"}
                  style={{
                    transition: "all 0.3s ease",
                  }}
                />
              </button>
              <span className={styles.actionCount}>{localLikesCount}</span>
            </div>
          )}
          <div className={styles.actionButton}>
            <button className={styles.actionBtn} onClick={handleShare}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
              </svg>
            </button>
            <span className={styles.actionCount}>Share</span>
          </div>

          <div className={styles.actionButton}>
            <button className={styles.actionBtn} onClick={toggleMute}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                {isMuted ? (
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                ) : (
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                )}
              </svg>
            </button>
            <span className={styles.actionCount}>
              {isMuted ? "Unmute" : "Mute"}
            </span>
          </div>
        </div>

        {/* Navigation Hints */}
        {hasUserInteracted && showControls && (
          <>
            {currentIndex < shorts.length - 1 && (
              <div className={styles.swipeHint}>
                <div className={styles.swipeUp}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                  </svg>
                  <span>Swipe up</span>
                </div>
              </div>
            )}

            {currentIndex > 0 && (
              <div className={styles.swipeHintDown}>
                <div className={styles.swipeDown}>
                  <span>Swipe down</span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                  </svg>
                </div>
              </div>
            )}
          </>
        )}

        {/* Bottom Info */}
        <div
          className={`${styles.bottomInfo} ${
            showControls ? styles.visible : ""
          }`}
        >
          <div className={styles.timeInfo}>
            <span>
              {formatTime(videoRef.current?.currentTime || 0)} /{" "}
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>

      {/* Share Menu */}
      {showShareMenu && (
        <div
          className={styles.shareMenu}
          onClick={() => setShowShareMenu(false)}
        >
          <div
            className={styles.shareMenuContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h4>Share this Short</h4>
            {shareOptions.map((option, index) => (
              <button
                key={index}
                className={styles.shareOption}
                onClick={() => {
                  option.action();
                  setShowShareMenu(false);
                }}
              >
                {option.name}
              </button>
            ))}
            <button
              className={styles.shareCancel}
              onClick={() => setShowShareMenu(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewShortsPlayer;
